/**
 * UranIA PA — `/chat-prova` route (US-030).
 *
 * Pagina di prova end-to-end per la pipeline AI Gateway (analisi cap. 12)
 * destinata a Tester / DevOps. Valida:
 *   - POST `/api/v1/gateway/invocations` con `Idempotency-Key` UUID v4
 *     (AC#1 + AC#2).
 *   - Apertura SSE su `GET /api/v1/gateway/invocations/{id}/stream`
 *     (AC#3 — endpoint deferred Fase 2; client degrada graceful e
 *     mostra comunque la risposta sincrona).
 *   - Render progressivo della risposta + collapsible Explain L1
 *     (utente) e L2 (auditor) AgID P.6 (AC#4).
 *   - Placeholder `<CitazioniSidebar />` (AC#5).
 *   - Toast con `Problem.detail` su 503 kill switch / servizio degradato
 *     (AC#6).
 *
 * Boundary di sicurezza:
 *   - La pagina e' riservata agli utenti autenticati con `tenantId`
 *     valorizzato (FR-F1-6: il Gateway rifiuta richieste senza
 *     `tenant_id` valido). UX fallback: `chat-prova-unauthenticated`
 *     coerente con le pagine admin (US-028/029).
 *   - Niente PII nei log: la pagina logga `tenant_id` e
 *     `idempotency_key` (UUID opaco) — mai il body utente.
 *
 * NB Fase 1: la pipeline backend e' wired only fino a US-027 (audit
 * integration). Il render del response e' "best-effort" (in Fase 1 la
 * provider chain restituisce ``provider_used="stub"``); il valore e'
 * comunque rappresentativo per validare il contratto SPA<->Gateway end-
 * to-end prima di Fase 2 (workspace Mod5).
 */

import { useTranslation } from 'react-i18next';
import { createRoute, Link } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { JSX, SyntheticEvent } from 'react';

import { CitazioniSidebar } from '../components/CitazioniSidebar.tsx';
import type { CitazioneSource } from '../components/CitazioniSidebar.tsx';
import {
  GatewayApiError,
  generateIdempotencyKey,
  openInvocationStream,
  submitInvocation,
} from '../lib/chat/gateway-api.ts';
import type {
  DataSensitivity,
  GatewayInvocationResponseDto,
  SubmitInvocationInput,
} from '../lib/chat/gateway-api.ts';
import { selectIsAuthenticated, selectPrincipal, useAuthStore } from '../lib/auth/auth-store.ts';
import { Route as RootRoute } from './__root.tsx';

const KILL_SWITCH_HTTP_STATUS = 503;
const DEFAULT_DATA_SENSITIVITY: DataSensitivity = 'internal';

/**
 * Estrae `sources` dalla risposta del Gateway per il rendering della
 * `<CitazioniSidebar />` (US-026 AC#2).  Quando il campo non e'
 * presente (Fase 1: backend non lo emette ancora) ritorna un array
 * vuoto -> empty state esplicito (AC#5).
 */
function selectSources(view: InvocationViewState | null): readonly CitazioneSource[] {
  if (view === null) {
    return [];
  }
  return view.snapshot.sources ?? [];
}

interface InvocationToastState {
  readonly title: string;
  readonly detail: string | null;
  readonly code: string | null;
}

interface InvocationViewState {
  readonly response: string;
  readonly streamedChunks: readonly string[];
  readonly snapshot: GatewayInvocationResponseDto;
}

function ToastError(props: {
  readonly toast: InvocationToastState;
  readonly onDismiss: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div
      className="urania-chat-prova__toast alert alert-danger"
      role="alert"
      aria-live="assertive"
      data-testid="chat-prova-toast-503"
    >
      <div className="d-flex justify-content-between align-items-start gap-3">
        <div>
          <strong className="d-block">{props.toast.title}</strong>
          {props.toast.detail !== null && (
            <p className="mb-0" data-testid="chat-prova-toast-503-detail">
              {props.toast.detail}
            </p>
          )}
          {props.toast.code !== null && (
            <p className="mb-0 small text-muted">
              <code>{props.toast.code}</code>
            </p>
          )}
        </div>
        <button
          type="button"
          className="btn btn-sm btn-outline-dark"
          onClick={props.onDismiss}
          data-testid="chat-prova-toast-503-dismiss"
        >
          {t('chat_prova.toast.dismiss')}
        </button>
      </div>
    </div>
  );
}

function ResponseExplain(props: { readonly view: InvocationViewState }): JSX.Element {
  const { t } = useTranslation();
  const { snapshot } = props.view;
  const explainL1Id = useId();
  const explainL2Id = useId();

  return (
    <section
      className="urania-chat-prova__response"
      data-testid="chat-prova-response"
      aria-live="polite"
    >
      <h2 className="h5 mb-2">{t('chat_prova.response.title')}</h2>
      <p className="text-muted small mb-2">
        {t('chat_prova.response.provider', { provider: snapshot.provider_used })}
        {' · '}
        {t('chat_prova.response.latency', { ms: snapshot.latency_ms })}
      </p>
      {snapshot.blocked && (
        <div className="alert alert-warning" role="alert" data-testid="chat-prova-response-blocked">
          <strong>{t('chat_prova.response.blocked.title')}</strong>
          {snapshot.question !== null && <p className="mb-0">{snapshot.question}</p>}
        </div>
      )}
      {snapshot.low_trust && !snapshot.blocked && (
        <div
          className="alert alert-warning"
          role="status"
          data-testid="chat-prova-response-low-trust"
        >
          <strong>{t('chat_prova.response.low_trust.title')}</strong>
          <p className="mb-0">{t('chat_prova.response.low_trust.body')}</p>
        </div>
      )}
      <pre
        className="urania-chat-prova__response-body bg-light p-3 rounded"
        data-testid="chat-prova-response-body"
      >
        {props.view.response}
      </pre>

      <details className="mb-2" data-testid="chat-prova-explain-l1">
        <summary id={explainL1Id} className="fw-bold">
          {t('chat_prova.explain.l1_title')}
        </summary>
        <div aria-labelledby={explainL1Id} className="mt-2">
          <pre className="bg-light p-2 rounded mb-0">{snapshot.explain_l1}</pre>
        </div>
      </details>

      <details data-testid="chat-prova-explain-l2">
        <summary id={explainL2Id} className="fw-bold">
          {t('chat_prova.explain.l2_title')}
        </summary>
        <div aria-labelledby={explainL2Id} className="mt-2">
          <pre className="bg-light p-2 rounded mb-0">{snapshot.explain_l2}</pre>
        </div>
      </details>
    </section>
  );
}

export function ChatProvaPage(): JSX.Element {
  const { t } = useTranslation();
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const principal = useAuthStore(selectPrincipal);
  const tenantId = principal?.tenantId ?? null;

  const queryFieldId = useId();
  const [query, setQuery] = useState('');
  const [toast, setToast] = useState<InvocationToastState | null>(null);
  const [view, setView] = useState<InvocationViewState | null>(null);
  const streamControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return (): void => {
      streamControllerRef.current?.abort();
    };
  }, []);

  const mutation = useMutation<
    GatewayInvocationResponseDto,
    GatewayApiError,
    SubmitInvocationInput
  >({
    mutationFn: async (input: SubmitInvocationInput) => {
      const idempotencyKey = generateIdempotencyKey();
      return submitInvocation(input, idempotencyKey);
    },
    onSuccess: (data) => {
      setToast(null);
      setView({
        response: data.response,
        streamedChunks: [],
        snapshot: data,
      });
      // AC#3 letterale: apre SSE per chunk progressivi. In Fase 1 il
      // backend stream non e' ancora deployato — onError silenzioso (la
      // risposta sincrona della POST e' gia' renderizzata sopra).
      const invocationId = data.invocation_id ?? null;
      if (invocationId !== null && invocationId.length > 0) {
        streamControllerRef.current?.abort();
        const controller = new AbortController();
        streamControllerRef.current = controller;
        void openInvocationStream(invocationId, {
          signal: controller.signal,
          onChunk: (chunk: string) => {
            setView((current) => {
              if (current === null) {
                return current;
              }
              return {
                ...current,
                streamedChunks: [...current.streamedChunks, chunk],
                response: current.response + chunk,
              };
            });
          },
          onError: () => {
            // Fase 1: endpoint stream deferred. Niente toast: la POST
            // sincrona ha gia' popolato la risposta (degradazione
            // graceful coerente con il contratto US-030 AC#3).
          },
        });
      }
    },
    onError: (error: GatewayApiError) => {
      // AC#6 letterale: 503 -> toast con Problem.detail.
      if (error.status === KILL_SWITCH_HTTP_STATUS) {
        setToast({
          title: t('chat_prova.toast.kill_switch_title'),
          detail: error.detail,
          code: error.code,
        });
        return;
      }
      setToast({
        title: t('chat_prova.toast.generic_title'),
        detail: error.detail ?? error.message,
        code: error.code,
      });
    },
  });

  const isSubmitDisabled = useMemo(() => {
    if (mutation.isPending) {
      return true;
    }
    if (query.trim().length === 0) {
      return true;
    }
    if (tenantId === null || tenantId.length === 0) {
      return true;
    }
    return false;
  }, [mutation.isPending, query, tenantId]);

  const handleSubmit = (event: SyntheticEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (tenantId === null || tenantId.length === 0) {
      return;
    }
    mutation.mutate({
      tenantId,
      query: query.trim(),
      dataSensitivity: DEFAULT_DATA_SENSITIVITY,
    });
  };

  if (!isAuthenticated) {
    return (
      <section className="urania-chat-prova" data-testid="chat-prova-unauthenticated">
        <h1 className="display-5 mb-3">{t('chat_prova.title')}</h1>
        <div
          className="alert alert-info"
          role="region"
          aria-label={t('chat_prova.unauthenticated.aria_label')}
        >
          <p className="mb-2">{t('chat_prova.unauthenticated.message')}</p>
          <Link to="/login" className="btn btn-primary" data-testid="chat-prova-go-to-login">
            {t('chat_prova.unauthenticated.go_to_login')}
          </Link>
        </div>
      </section>
    );
  }

  if (tenantId === null || tenantId.length === 0) {
    return (
      <section className="urania-chat-prova" data-testid="chat-prova-no-tenant">
        <h1 className="display-5 mb-3">{t('chat_prova.title')}</h1>
        <div className="alert alert-warning" role="alert" data-testid="chat-prova-no-tenant-alert">
          <strong>{t('chat_prova.no_tenant.title')}</strong>
          <p className="mb-0">{t('chat_prova.no_tenant.body')}</p>
        </div>
      </section>
    );
  }

  return (
    <section className="urania-chat-prova" data-testid="chat-prova-page">
      <h1 className="display-5 mb-2">{t('chat_prova.title')}</h1>
      <p className="lead mb-4">{t('chat_prova.subtitle')}</p>

      {toast !== null && (
        <ToastError
          toast={toast}
          onDismiss={(): void => {
            setToast(null);
          }}
        />
      )}

      <div className="row g-3">
        <div className="col-12 col-lg-8">
          <form
            className="urania-chat-prova__form"
            data-testid="chat-prova-form"
            onSubmit={handleSubmit}
            noValidate
          >
            <div className="mb-3">
              <label htmlFor={queryFieldId} className="form-label">
                {t('chat_prova.form.query_label')}
              </label>
              <textarea
                id={queryFieldId}
                className="form-control"
                rows={5}
                required
                value={query}
                onChange={(event): void => {
                  setQuery(event.target.value);
                }}
                data-testid="chat-prova-query"
                placeholder={t('chat_prova.form.query_placeholder')}
                aria-describedby={`${queryFieldId}-hint`}
              />
              <div id={`${queryFieldId}-hint`} className="form-text">
                {t('chat_prova.form.query_hint')}
              </div>
            </div>

            <button
              type="submit"
              className="btn btn-primary"
              data-testid="chat-prova-submit"
              disabled={isSubmitDisabled}
            >
              {mutation.isPending ? t('chat_prova.form.submitting') : t('chat_prova.form.submit')}
            </button>
          </form>

          {view !== null && <ResponseExplain view={view} />}
        </div>

        <div className="col-12 col-lg-4">
          <CitazioniSidebar sources={selectSources(view)} />
        </div>
      </div>
    </section>
  );
}

export const Route = createRoute({
  getParentRoute: () => RootRoute,
  path: '/chat-prova',
  component: ChatProvaPage,
});
